import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../../../context/auth-provider';
import { can, PERMISSIONS } from '../../../../auth/permissions';
import { SegmentedControl } from '../../../segmented-control/segmented-control';
import { setBuildingStays, StaysApiError } from '../../../../api/stays-client';
import toast from '../../../../utils/toast';
import { hasActiveText } from '../dashboard-stays-components/stays-labels';
import {
  UnitsSection,
  UnitsSectionHead,
  UnitsSectionNote,
  UnitsSectionTitle,
} from './dashboard-buildings.styled';

/**
 * LOBBERO STAYS — the building's consent switch (ai-plans/stays plan S1,
 * screen S-6; migration 142 properties.stays_enabled).
 *
 * Off / On. It saves the moment it changes, through its own route
 * (PUT /api/stays/buildings/:id/settings, stays:promote) — not the building
 * form's Save, which is building:edit and multipart and which property
 * managers do not hold. Disabled, with the reason, without stays:promote.
 *
 * Turning it OFF is refused while a listing here is live (409
 * STAYS_BUILDING_HAS_ACTIVE with the count): the control goes back to On and
 * the toast says how many to pause or retire first.
 *
 * The caller mounts it only for a real building id, stays:read and an
 * organisation that has Stays.
 */

type OnOff = 'off' | 'on';
const OPTIONS: ReadonlyArray<{ value: OnOff; label: string }> = [
  { value: 'off', label: 'Off' },
  { value: 'on', label: 'On' },
];

export interface BuildingStaysSectionProps {
  buildingId: number;
  buildingName: string;
  /** The building row's stays_enabled, as last read. */
  enabled: boolean | undefined;
  onChanged?: (enabled: boolean) => void;
}

export const BuildingStaysSection: React.FC<BuildingStaysSectionProps> = ({
  buildingId,
  buildingName,
  enabled,
  onChanged,
}) => {
  const { permissions }: { permissions: string[] } = useAuth();
  const canChange = can(permissions, PERMISSIONS.STAYS_PROMOTE);
  const [value, setValue] = useState<OnOff>(enabled ? 'on' : 'off');
  const [saving, setSaving] = useState(false);
  const savedRef = useRef<OnOff>(enabled ? 'on' : 'off');

  useEffect(() => {
    savedRef.current = enabled ? 'on' : 'off';
    setValue(savedRef.current);
  }, [buildingId, enabled]);

  const choose = async (next: OnOff) => {
    if (saving || next === savedRef.current) return;
    setValue(next);
    setSaving(true);
    try {
      const saved = await setBuildingStays(buildingId, next === 'on');
      savedRef.current = saved.stays_enabled ? 'on' : 'off';
      setValue(savedRef.current);
      toast.success(
        saved.stays_enabled ? `Stays is on for ${buildingName}.` : `Stays is off for ${buildingName}.`
      );
      onChanged?.(saved.stays_enabled);
    } catch (error) {
      setValue(savedRef.current);
      if (error instanceof StaysApiError && error.code === 'STAYS_BUILDING_HAS_ACTIVE') {
        toast.error(hasActiveText(Number(error.extra.count) || 0));
      } else {
        toast.error('Could not save the Stays setting. Try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <UnitsSection aria-label={`Lobbero Stays in ${buildingName}`}>
      <UnitsSectionHead>
        <UnitsSectionTitle>Lobbero Stays</UnitsSectionTitle>
      </UnitsSectionHead>
      <SegmentedControl
        label="Short-term stays in this building"
        options={OPTIONS}
        value={value}
        onChange={next => {
          void choose(next);
        }}
        disabled={!canChange || saving}
      />
      <UnitsSectionNote>
        {value === 'on'
          ? 'Owners and property managers can list units here for short-term guests.'
          : 'No unit in this building can be listed for short stays.'}
        {canChange ? '' : ' Only an owner or a property manager can change this.'}
      </UnitsSectionNote>
    </UnitsSection>
  );
};
